/**
 * hls.js tuning for the vertical clips feed (NEX-9360).
 *
 * Plain module, shared by every VideoPlayer on the page. It must never import `hls.js`: that
 * chunk stays lazy, and only downloads for a viewer who actually meets an adaptive clip.
 */
import { ref } from 'vue'

export const DEFAULT_START_ESTIMATE_BPS = 1_500_000
export const MIN_START_ESTIMATE_BPS = 700_000     // 360 rung
export const MAX_START_ESTIMATE_BPS = 5_000_000   // above the 1080 rung

// Buffer target while the next slide prefetches: below one fragment, so the first fragment
// that lands already exceeds it and hls.js asks for no second one.
export const PREFETCH_MAX_BUFFER_SECONDS = 1

// Headroom on the browser's own downlink figure, which is optimistic and heavily rounded.
const DOWNLINK_SAFETY_FACTOR = 0.8

/** true while the current feed slide is playing. Written only by VideoPlayer. */
export const currentSlidePlaying = ref(false)

// One estimate per page, so a swipe starts on the rung the last clip actually sustained
// instead of the hls.js 500 kbps guess.
let lastBandwidthEstimate = null

/** Last measured estimate across every VideoPlayer on the page, or null. */
export function recalledBandwidthEstimate() {
    return lastBandwidthEstimate
}

/** Stores `bps` when finite and > 0; ignores anything else. */
export function rememberBandwidthEstimate(bps) {
    if (typeof bps === 'number' && Number.isFinite(bps) && bps > 0) {
        lastBandwidthEstimate = bps
    }
}

const connection = () => (typeof navigator !== 'undefined' ? navigator.connection : undefined)

/**
 * Seed for a new instance, first match wins: the page's recalled estimate, the browser's
 * downlink hint (clamped), then a flat default.
 */
export function startEstimate() {
    const recalled = recalledBandwidthEstimate()

    if (recalled !== null) {
        return recalled
    }

    try {
        const downlinkMbps = Number(connection()?.downlink)

        if (Number.isFinite(downlinkMbps) && downlinkMbps > 0) {
            const bps = downlinkMbps * 1_000_000 * DOWNLINK_SAFETY_FACTOR

            return Math.min(MAX_START_ESTIMATE_BPS, Math.max(MIN_START_ESTIMATE_BPS, bps))
        }
    } catch {
        // Some embedded browsers throw on navigator.connection; fall through to the default.
    }

    return DEFAULT_START_ESTIMATE_BPS
}

/** false on Save-Data or a 2G-class connection; true otherwise, including when unknown. */
export function prefetchAllowed() {
    try {
        const info = connection()

        if (!info) {
            return true
        }

        if (info.saveData === true) {
            return false
        }

        return info.effectiveType !== 'slow-2g' && info.effectiveType !== '2g'
    } catch {
        return true
    }
}

/**
 * true only on Apple WebKit (Safari, and every iOS browser): the AirPlay API exists nowhere else.
 * Those keep native HLS for AirPlay and AVFoundation's battery-efficient pipeline.
 */
export function preferNativeHls() {
    try {
        return typeof window !== 'undefined' && 'WebKitPlaybackTargetAvailabilityEvent' in window
    } catch {
        return false
    }
}

/** true when Media Source Extensions can play the ladder's H.264/AAC, i.e. hls.js can run. */
export function mseAvailable() {
    try {
        const MediaSourceImpl = window.MediaSource ?? window.ManagedMediaSource

        return typeof MediaSourceImpl?.isTypeSupported === 'function'
            && MediaSourceImpl.isTypeSupported('video/mp4; codecs="avc1.42E01E,mp4a.40.2"') === true
    } catch {
        return false
    }
}

/**
 * true when this browser can decode HEVC, i.e. may be offered the H.265 master
 * (docs/nex-9360-clip-hevc-renditions.md). Ported from the trailer player's probe
 * (partials/hls-quality-selector.blade.php): MSE first, since that is what hls.js feeds, then
 * the element, since Safari plays HEVC natively while its MSE support has lagged. hvc1 and hev1
 * are the two sample entry names HEVC ships under; a browser may know one and not the other.
 */
export function canDecodeHevc() {
    const types = [
        'video/mp4; codecs="hvc1.1.6.L93.B0"',
        'video/mp4; codecs="hev1.1.6.L93.B0"',
    ]

    try {
        const MediaSourceImpl = window.MediaSource ?? window.ManagedMediaSource

        if (typeof MediaSourceImpl?.isTypeSupported === 'function'
            && types.some((type) => MediaSourceImpl.isTypeSupported(type))) {
            return true
        }
    } catch {
        // Fall through to the element probe.
    }

    try {
        const video = document.createElement('video')

        return types.some((type) => video.canPlayType(type) !== '')
    } catch {
        // No DOM (SSR) or an embedded browser that throws: H.264 is always safe.
        return false
    }
}

// The forward-buffer byte budget. hls.js targets
// min(max(8 * maxBufferSize / levelBitrate, maxBufferLength), maxMaxBufferLength) seconds
// (base-stream-controller getMaxBufferLength), so its 60 MB default outvotes maxBufferLength on
// every rung and the buffer ran to the 30 s ceiling. 600 KB makes maxBufferLength (10 s) bind on
// every rung from 480 kbps up and allows 12 s on the ~400 kbps 240 rung. hls.js then overshoots
// its target by up to one 4 s fragment, which keeps the measured peak at ≤ 16 s.
const MAX_BUFFER_BYTES = 600_000

/**
 * Config for `new Hls(...)`. Only these keys; everything else stays at the hls.js default —
 * `backBufferLength` included (Infinity), so a looping clip replays without refetching.
 *
 * The GapController stall keys (nudgeOffset, nudgeMaxRetry, highBufferWatchdogPeriod,
 * detectStallWithCurrentTimeMs) stay at their defaults on purpose: the H.264/TS freeze sits inside
 * one contiguous buffered range, which hls.js 1.7.3 never nudges whatever their values, so the
 * player's stall watchdog handles it (docs/nex-9360-clip-hls-player-hardening.md, WU-5 finding).
 */
export function buildClipHlsConfig() {
    return {
        // Segment loading is driven by ClipPlayer's syncHlsLoad(), per slide role.
        autoStartLoad: false,
        startLevel: -1,
        abrEwmaDefaultEstimate: startEstimate(),
        // The start level comes from the seeded estimate. A bandwidth test would fetch the first
        // fragment on the lowest rung and then again on the rung it picks.
        testBandwidth: false,
        // hls.js caps at the first rung that covers the box on EITHER side, which held a 560x900
        // desktop player to the 540x960 rung. ClipPlayer sets autoLevelCapping from
        // capLevelForPlayer() instead.
        capLevelToPlayerSize: false,
        // Half-lives in seconds of fragment LOAD time, not playback. hls.js uses the minimum of the
        // two averages, so the shorter half-life sets how hard one slow fragment drags the estimate
        // down and the longer one how fast it climbs back; equal values trade best. Measured on the
        // real CDN, 60 s at 500 kbps then 5 Mbps, ≥ 720 on screen needed within 15 s. The 3/9
        // defaults never got there in 30 s. 1/1 and 0.75/0.75 missed it on H.264 in one run each
        // (20.9 s, 16.2 s): the 720 switch has to land while the playhead is still starved, or the
        // ~10 s buffered below it plays out first. 0.6/0.6 is the least aggressive pair that
        // passed every run: 5.9 / 11.4 s (H.264) and 12.1 / 12.2 s (H.265), with 0 sn fetched at
        // two levels during start-up. A start-up duplicate, when one happens, is hls.js's emergency
        // abandon of sn2, which no half-life reaches: one sample in, the estimate is that sample.
        abrEwmaFastVoD: 0.6,
        abrEwmaSlowVoD: 0.6,
        maxBufferLength: 10,
        maxBufferSize: MAX_BUFFER_BYTES,
        // A swipe feed: anything buffered past this is likely thrown away by the next swipe.
        maxMaxBufferLength: 15,
        lowLatencyMode: false,
    }
}

/**
 * Index into `levels` (hls.js order, lowest bitrate first) to pass to `hls.autoLevelCapping`: the
 * lowest rung that covers the player box in device pixels (`dpr`) on both width and height; -1 (no
 * cap) when that is already the top, when nothing covers the box, or when the box has no size.
 * A 560x900 box at 1 DPR caps at 720x1280 (540x960 is too narrow); high-DPI screens and phones
 * whose box no rung covers keep the whole ladder, 1080 included.
 */
export function capLevelForPlayer(levels, cssWidth, cssHeight, dpr) {
    const list = Array.isArray(levels) ? levels : []
    const ratio = Number.isFinite(dpr) && dpr > 0 ? dpr : 1
    const width = cssWidth * ratio
    const height = cssHeight * ratio

    if (!list.length || !(width > 0) || !(height > 0)) {
        return -1
    }

    const covering = list
        .filter((level) => level?.width >= width && level?.height >= height)
        .reduce((smallest, level) => Math.min(smallest, level.height), Infinity)

    if (!Number.isFinite(covering)) {
        return -1
    }

    // The highest-bitrate level at or below the covering height.
    let cap = -1

    list.forEach((level, index) => {
        if (level?.height <= covering) {
            cap = index
        }
    })

    return cap === -1 || cap === list.length - 1 ? -1 : cap
}
